import { useState, useEffect } from "react";
import { api, extractErrorMessage } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const STATUS_LABEL = { not_started: "Not started", in_progress: "In progress", complete: "Complete" };
const Row = ({ label, value }) => (value ? (
  <div className="flex justify-between gap-4 border-b border-slate-100 py-1 last:border-0 text-sm">
    <span className="text-slate-500">{label}</span><span className="text-right text-slate-800">{value}</span>
  </div>
) : null);

export const SkipTracePanel = ({ order, role, onChanged }) => {
  const st = order?.skip_trace;
  const isAdmin = role === "admin";
  const paid = order?.invoice?.payment_status === "paid";
  const [status, setStatus] = useState(st?.status || "not_started");
  const [res, setRes] = useState({ new_addresses: "", phone: "", employer: "", notes: "", source_vendor: "", vendor_cost: "", ...(st?.result || {}) });
  const [noResult, setNoResult] = useState(!!st?.no_result);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setStatus(st?.status || "not_started"); setNoResult(!!st?.no_result); setRes({ new_addresses: "", phone: "", employer: "", notes: "", source_vendor: "", vendor_cost: "", ...(st?.result || {}) }); }, [st]);
  if (!st) return null;
  const inp = st.input || {};

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.patch(`/admin/orders/${order.id}/skip-trace`, { status, result: res, no_result: noResult });
      toast.success("Skip trace saved");
      onChanged && onChanged(data);
    } catch (e) { toast.error(extractErrorMessage(e, "Could not save skip trace")); }
    finally { setBusy(false); }
  };

  return (
    <div className="rounded-xl border border-amber-200 bg-white p-5" data-testid="skip-trace-panel">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="font-display text-base font-bold text-slate-900">Skip Trace <span className="text-xs font-normal text-slate-400">({st.tier})</span></h3>
        <span data-testid="skip-trace-status" className={`rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${st.status === "complete" ? "bg-emerald-50 text-emerald-700" : st.status === "in_progress" ? "bg-indigo-50 text-indigo-700" : "bg-slate-100 text-slate-600"}`}>{STATUS_LABEL[st.status] || st.status}</span>
      </div>

      {isAdmin && !paid && (
        <div data-testid="skip-trace-awaiting-payment" className="mb-3 rounded-md border border-amber-200 bg-amber-50 p-2 text-xs text-amber-800">Awaiting payment — skip trace can be worked once the client's invoice is paid.</div>
      )}

      {inp && (
        <div className="mb-3">
          <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">Client-submitted information</p>
          <Row label="Defendant name" value={inp.defendant_name} />
          <Row label="Aliases / maiden" value={inp.aliases} />
          <Row label="Last known address" value={inp.last_known_address} />
          <Row label="Date last valid" value={inp.address_last_valid} />
          <Row label="DOB" value={inp.dob} />
          <Row label="SSN (last 4)" value={inp.ssn_last4} />
          <Row label="Phone" value={inp.phone} />
          <Row label="Email" value={inp.email} />
          <Row label="Employer" value={inp.employer} />
          <Row label="Vehicle" value={inp.vehicle} />
          <Row label="Other leads" value={inp.other_leads} />
          <Row label="Prior attempts" value={inp.prior_attempts} />
          {st.permissible_purpose && <p className="mt-1 text-[11px] text-slate-400">Permissible purpose certified {st.permissible_purpose.at ? new Date(st.permissible_purpose.at).toLocaleString() : ""}{st.permissible_purpose.ip ? ` · IP ${st.permissible_purpose.ip}` : ""}</p>}
        </div>
      )}

      {isAdmin ? (
        <div className="space-y-2 border-t border-slate-100 pt-3">
          <div>
            <Label>Status</Label>
            <select data-testid="skip-trace-status-select" value={status} onChange={(e) => setStatus(e.target.value)} disabled={!paid} className="w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm">
              <option value="not_started">Not started</option>
              <option value="in_progress">In progress</option>
              <option value="complete">Complete</option>
            </select>
          </div>
          <div className="grid gap-2 sm:grid-cols-2">
            <div className="sm:col-span-2"><Label>New address(es)</Label><Textarea data-testid="skip-trace-new-addresses" rows={2} value={res.new_addresses} onChange={(e) => setRes({ ...res, new_addresses: e.target.value })} disabled={!paid} /></div>
            <div><Label>Phone</Label><Input data-testid="skip-trace-res-phone" value={res.phone} onChange={(e) => setRes({ ...res, phone: e.target.value })} disabled={!paid} /></div>
            <div><Label>Employer</Label><Input data-testid="skip-trace-res-employer" value={res.employer} onChange={(e) => setRes({ ...res, employer: e.target.value })} disabled={!paid} /></div>
            <div className="sm:col-span-2"><Label>Notes</Label><Textarea data-testid="skip-trace-res-notes" rows={2} value={res.notes} onChange={(e) => setRes({ ...res, notes: e.target.value })} disabled={!paid} /></div>
            <div><Label>Source / vendor used</Label><Input data-testid="skip-trace-res-vendor" value={res.source_vendor} onChange={(e) => setRes({ ...res, source_vendor: e.target.value })} disabled={!paid} /></div>
            <div><Label>Vendor cost (internal)</Label><Input data-testid="skip-trace-res-cost" value={res.vendor_cost} onChange={(e) => setRes({ ...res, vendor_cost: e.target.value })} disabled={!paid} /></div>
          </div>
          {status === "complete" && (
            <label className="flex items-center gap-2 text-sm text-slate-700" data-testid="skip-trace-no-result-toggle">
              <input type="checkbox" checked={noResult} onChange={(e) => setNoResult(e.target.checked)} disabled={!paid} className="h-4 w-4" />
              No result found — client is told no new information was found
            </label>
          )}
          <Button data-testid="skip-trace-save" onClick={save} disabled={busy || !paid} className="w-full bg-amber-600 text-white hover:bg-amber-700">{busy ? "Saving…" : "Save skip trace"}</Button>
        </div>
      ) : (
        st.status === "complete" ? (
          st.no_result ? (
            <div className="border-t border-slate-100 pt-3" data-testid="skip-trace-client-noresult">
              <p className="text-sm text-slate-600">No new information was found for this search.</p>
            </div>
          ) : (
            <div className="border-t border-slate-100 pt-3" data-testid="skip-trace-client-result">
              <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">Results</p>
              <Row label="New address(es)" value={st.result?.new_addresses} />
              <Row label="Phone" value={st.result?.phone} />
              <Row label="Employer" value={st.result?.employer} />
              <Row label="Notes" value={st.result?.notes} />
            </div>
          )
        ) : (
          <p className="border-t border-slate-100 pt-3 text-sm text-slate-500" data-testid="skip-trace-client-pending">Results will appear here once ARPS completes the skip trace.</p>
        )
      )}
    </div>
  );
};
